import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Mail, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Send, 
  RefreshCw,
  Shield,
  Server,
  Globe,
  PenLine,
  Save,
  Eye,
  Loader2,
  KeyRound,
  Trash2
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const LISTED_ELSEWHERE = new Set(['{{monthly_fee}}', '{{invoice_number}}', '{{payment_method}}', '{{tournament_name}}', '{{age_category}}']);

const EmailSettings = () => {
  const [billingPlaceholders, setBillingPlaceholders] = useState([]);
  useEffect(() => {
    api.get('/email/billing-placeholders').then((r) => setBillingPlaceholders(r.data?.placeholders || [])).catch(() => {});
  }, []);
  const [emailStatus, setEmailStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Signature state
  const [signature, setSignature] = useState({
    business_name: '',
    address: '',
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    facebook: '',
    instagram: '',
    twitter: ''
  });
  const [savingSignature, setSavingSignature] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // iter310 — Resend BYOK per-tenant settings
  const [byok, setByok] = useState(null);              // { saved, resolved }
  const [byokForm, setByokForm] = useState({ api_key: '', from_email: '', active: true });
  const [byokSaving, setByokSaving] = useState(false);
  const [byokTesting, setByokTesting] = useState(false);
  const [byokTestEmail, setByokTestEmail] = useState('');
  const [byokDeleting, setByokDeleting] = useState(false);

  useEffect(() => {
    fetchEmailStatus();
    fetchSignatureSettings();
    fetchByokSettings();
  }, []);

  const fetchByokSettings = async () => {
    try {
      const { data } = await api.get('/admin/email/resend-settings');
      setByok(data);
      setByokForm((prev) => ({
        ...prev,
        from_email: data?.saved?.from_email || '',
        active: data?.saved?.active !== false,
      }));
    } catch (e) {
      // 404/403 acceptable on first load
    }
  };

  const saveByok = async () => {
    setByokSaving(true);
    try {
      const payload = {
        from_email: byokForm.from_email || null,
        active: byokForm.active,
      };
      if (byokForm.api_key && byokForm.api_key.trim()) {
        payload.api_key = byokForm.api_key.trim();
      }
      await api.post('/admin/email/resend-settings', payload);
      toast.success('Resend BYOK settings saved');
      setByokForm((prev) => ({ ...prev, api_key: '' }));
      await fetchByokSettings();
      await fetchEmailStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save Resend settings');
    } finally {
      setByokSaving(false);
    }
  };

  const deleteByok = async () => {
    if (!window.confirm('Revert to platform Resend key? Your custom credentials will be removed.')) return;
    setByokDeleting(true);
    try {
      await api.delete('/admin/email/resend-settings');
      toast.success('Reverted to platform Resend key');
      setByokForm({ api_key: '', from_email: '', active: true });
      await fetchByokSettings();
      await fetchEmailStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revert');
    } finally {
      setByokDeleting(false);
    }
  };

  const testByok = async () => {
    if (!byokTestEmail) {
      toast.error('Enter a destination email to test');
      return;
    }
    setByokTesting(true);
    try {
      const { data } = await api.post('/admin/email/resend-settings/test', { to_email: byokTestEmail });
      toast.success(`Test email sent (${data.api_key_masked}). Check ${byokTestEmail}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setByokTesting(false);
    }
  };

  const fetchEmailStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/email/status');
      setEmailStatus(response.data);
    } catch (error) {
      toast.error('Failed to fetch email status');
      setEmailStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchSignatureSettings = async () => {
    try {
      const response = await api.get('/admin/email/signature');
      if (response.data) {
        setSignature({
          business_name: response.data.business_name || '',
          address: response.data.address || '',
          phone: response.data.phone || '',
          whatsapp: response.data.whatsapp || '',
          email: response.data.email || '',
          website: response.data.website || '',
          facebook: response.data.facebook || '',
          instagram: response.data.instagram || '',
          twitter: response.data.twitter || ''
        });
      }
    } catch (error) {
      console.log('No signature settings found, using defaults');
    }
  };

  const saveSignature = async () => {
    setSavingSignature(true);
    try {
      await api.put('/admin/email/signature', signature);
      toast.success('Email signature saved successfully!');
    } catch (error) {
      toast.error('Failed to save signature');
    } finally {
      setSavingSignature(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    setTestResult(null);
    try {
      const response = await api.post('/admin/email/test');
      setTestResult({ success: true, message: response.data.message });
      toast.success('Test email sent successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send test email';
      setTestResult({ success: false, message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setSendingTest(false);
    }
  };

  const generateSignaturePreview = () => {
    const formatPhone = (phone) => {
      if (!phone) return '';
      const clean = phone.replace(/\D/g, '');
      if (clean.length === 10) {
        return `(${clean.slice(0,3)}) ${clean.slice(3,6)}-${clean.slice(6)}`;
      }
      return phone;
    };

    return (
      <div style={{ fontFamily: 'Arial, sans-serif', marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
        <p style={{ margin: 0, color: '#1e3a5f', fontWeight: 'bold', fontSize: '16px' }}>
          {signature.business_name || 'Your Business Name'}
        </p>
        {signature.address && (
          <p style={{ margin: '5px 0', color: '#64748b', fontSize: '13px' }}>
            📍 {signature.address}
          </p>
        )}
        <p style={{ margin: '8px 0', fontSize: '14px' }}>
          {signature.phone && (
            <span style={{ marginRight: '10px' }}>
              📞 <a href={`tel:${signature.phone}`} style={{ color: '#64748b', textDecoration: 'none' }}>
                {formatPhone(signature.phone)}
              </a>
            </span>
          )}
          {signature.whatsapp && (
            <span style={{ marginRight: '10px' }}>
              💬 <a href={`https://wa.me/${signature.whatsapp.replace(/\D/g, '')}`} style={{ color: '#25D366', textDecoration: 'none' }}>
                WhatsApp
              </a>
            </span>
          )}
          {signature.email && (
            <span style={{ marginRight: '10px' }}>
              ✉️ <a href={`mailto:${signature.email}`} style={{ color: '#64748b', textDecoration: 'none' }}>
                {signature.email}
              </a>
            </span>
          )}
          {signature.website && (
            <span>
              🌐 <a href={signature.website} style={{ color: '#2563eb', textDecoration: 'none' }}>
                {signature.website.replace('https://', '').replace('http://', '').replace(/\/$/, '')}
              </a>
            </span>
          )}
        </p>
        {(signature.facebook || signature.instagram || signature.twitter) && (
          <p style={{ margin: '10px 0 0 0', fontSize: '13px' }}>
            {signature.facebook && (
              <a href={signature.facebook} style={{ color: '#1877F2', textDecoration: 'none', marginRight: '10px' }}>
                Facebook
              </a>
            )}
            {signature.instagram && (
              <a href={signature.instagram} style={{ color: '#E4405F', textDecoration: 'none', marginRight: '10px' }}>
                Instagram
              </a>
            )}
            {signature.twitter && (
              <a href={signature.twitter} style={{ color: '#1DA1F2', textDecoration: 'none' }}>
                Twitter
              </a>
            )}
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="Email Settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  const isConfigured = emailStatus?.resend_configured;
  const isUsingCustomDomain = emailStatus?.delivery_email && !emailStatus.delivery_email.includes('resend.dev');

  return (
    <DashboardLayout title="Email Settings">
      <div className="space-y-6" data-testid="email-settings-page">
        {/* Header */}
        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Email Configuration</h1>
            <p className="text-slate-500">Monitor and test your email delivery settings</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={fetchEmailStatus} data-testid="refresh-status-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Status
          </Button>
          </div>
        </div>

        {/* iter340 — Branded sending identity (per-tenant verified domain) */}
        <EmailIdentityCard />

        {/* Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* API Key Status */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${isConfigured ? 'bg-green-100' : 'bg-red-100'}`}>
                  {isConfigured ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Resend API Key</p>
                  <p className="font-semibold">
                    {isConfigured ? 'Configured' : 'Not Configured'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Domain */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${isUsingCustomDomain ? 'bg-green-100' : 'bg-amber-100'}`}>
                  {isUsingCustomDomain ? (
                    <Globe className="h-6 w-6 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Sender Domain</p>
                  <p className="font-semibold">
                    {isUsingCustomDomain ? 'Custom Domain' : 'Default Domain'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Overall Status */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${isConfigured ? 'bg-green-100' : 'bg-red-100'}`}>
                  <Mail className="h-6 w-6 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email Status</p>
                  <Badge className={isConfigured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                    {isConfigured ? 'Operational' : 'Not Working'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Configuration Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              Configuration Details
            </CardTitle>
            <CardDescription>
              Current email service configuration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* API Key — reflect the ACTIVE source (tenant BYOK key when
                  configured, else the platform key) so this never shows a
                  platform key that isn't actually being used. */}
              <div className="flex items-center justify-between py-3 border-b">
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="font-medium">RESEND_API_KEY</p>
                    <p className="text-sm text-slate-500">
                      {byok?.resolved?.source === 'tenant'
                        ? 'Your own Resend key (BYOK) is active'
                        : 'API key for Resend email service'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {byok?.resolved?.source === 'tenant' ? (
                    <div>
                      <Badge className="bg-indigo-100 text-indigo-700">Your key (BYOK)</Badge>
                      {byok?.resolved?.api_key_masked && (
                        <p className="text-xs text-slate-500 mt-1 font-mono">{byok.resolved.api_key_masked}</p>
                      )}
                    </div>
                  ) : isConfigured ? (
                    <div>
                      <Badge className="bg-green-100 text-green-700">Platform key</Badge>
                      <p className="text-xs text-slate-500 mt-1 font-mono">
                        {emailStatus.resend_key_prefix}
                      </p>
                    </div>
                  ) : (
                    <Badge className="bg-red-100 text-red-700">Missing</Badge>
                  )}
                </div>
              </div>

              {/* Delivery Email */}
              <div className="flex items-center justify-between py-3 border-b">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="font-medium">From Address</p>
                    <p className="text-sm text-slate-500">Email address used for sending</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm">
                    {(byok?.resolved?.source === 'tenant' && byok?.resolved?.from_email)
                      || emailStatus?.delivery_email || 'Not set'}
                  </p>
                  {isUsingCustomDomain ? (
                    <Badge className="bg-green-100 text-green-700 mt-1">Custom Domain</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 mt-1">Default</Badge>
                  )}
                </div>
              </div>

              {/* Fallback */}
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <RefreshCw className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="font-medium">Fallback Address</p>
                    <p className="text-sm text-slate-500">Used if custom domain fails</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm">{emailStatus?.default_from || 'noreply@resend.dev'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test Email */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Test Email Delivery
            </CardTitle>
            <CardDescription>
              Send a test email to verify your configuration is working
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">
                  Click the button below to send a test email to your admin email address.
                  This will verify that emails can be delivered successfully.
                </p>
              </div>

              <Button 
                onClick={sendTestEmail} 
                disabled={!isConfigured || sendingTest}
                className="w-full sm:w-auto"
                data-testid="send-test-email-btn"
              >
                {sendingTest ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test Email
                  </>
                )}
              </Button>

              {!isConfigured && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800">Email Not Configured</p>
                      <p className="text-sm text-red-700 mt-1">
                        The RESEND_API_KEY environment variable is not set. 
                        Please add it to your deployment configuration.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {testResult && (
                <div className={`border rounded-lg p-4 ${
                  testResult.success 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {testResult.success ? (
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                    )}
                    <div>
                      <p className={`font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                        {testResult.success ? 'Test Email Sent!' : 'Test Failed'}
                      </p>
                      <p className={`text-sm mt-1 ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                        {testResult.message}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* iter310 — Resend BYOK */}
        <Card data-testid="resend-byok-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Your Own Resend Key (BYOK)
            </CardTitle>
            <CardDescription>
              Use your tenant&apos;s own Resend account for outgoing email instead of the platform default. Your API key is encrypted at rest.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active source</p>
                  <p className="font-semibold capitalize" data-testid="byok-source">
                    {byok?.resolved?.source === 'tenant'
                      ? 'Your Resend key'
                      : byok?.resolved?.source === 'platform'
                        ? 'Platform fallback'
                        : 'Not configured'}
                  </p>
                  {byok?.resolved?.api_key_masked && (
                    <p className="text-xs text-slate-500 mt-1 font-mono">{byok.resolved.api_key_masked}</p>
                  )}
                  {byok?.resolved?.from_email && (
                    <p className="text-xs text-slate-500">From: {byok.resolved.from_email}</p>
                  )}
                </div>
                {byok?.saved?.has_api_key && (
                  <Badge className="bg-green-100 text-green-700">Custom key saved</Badge>
                )}
              </div>

              <div>
                <Label htmlFor="byok_api_key">Resend API Key</Label>
                <Input
                  id="byok_api_key"
                  type="password"
                  placeholder={byok?.saved?.has_api_key ? 'Leave blank to keep current key' : 're_xxx…'}
                  value={byokForm.api_key}
                  onChange={(e) => setByokForm({ ...byokForm, api_key: e.target.value })}
                  data-testid="byok-api-key-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Get yours at{' '}
                  <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                    resend.com/api-keys
                  </a>
                </p>
              </div>

              <div>
                <Label htmlFor="byok_from_email">From Email</Label>
                <Input
                  id="byok_from_email"
                  type="email"
                  placeholder="hello@yourdomain.com"
                  value={byokForm.from_email}
                  onChange={(e) => setByokForm({ ...byokForm, from_email: e.target.value })}
                  data-testid="byok-from-email-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Must be from a domain you&apos;ve verified in Resend.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <Button onClick={saveByok} disabled={byokSaving} data-testid="byok-save-btn">
                  {byokSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save
                </Button>
                {byok?.saved?.has_api_key && (
                  <Button variant="outline" onClick={deleteByok} disabled={byokDeleting} data-testid="byok-revert-btn">
                    {byokDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    Revert to Platform Key
                  </Button>
                )}
              </div>

              <div className="border-t pt-4 mt-2">
                <Label htmlFor="byok_test_email">Smoke Test</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="byok_test_email"
                    type="email"
                    placeholder="you@example.com"
                    value={byokTestEmail}
                    onChange={(e) => setByokTestEmail(e.target.value)}
                    data-testid="byok-test-email-input"
                  />
                  <Button onClick={testByok} disabled={byokTesting} variant="outline" data-testid="byok-test-btn">
                    {byokTesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    Send Test
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Sends a tiny verification email using the currently-resolved credentials.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Signature Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PenLine className="h-5 w-5" />
              Email Signature
            </CardTitle>
            <CardDescription>
              Configure the signature that appears at the bottom of all outgoing emails
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Form */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    value={signature.business_name}
                    onChange={(e) => setSignature({...signature, business_name: e.target.value})}
                    placeholder="Delhi Capitals Cricket Academy NJ"
                  />
                </div>
                
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={signature.address}
                    onChange={(e) => setSignature({...signature, address: e.target.value})}
                    placeholder="123 Main St, City, State 12345"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={signature.phone}
                      onChange={(e) => setSignature({...signature, phone: e.target.value})}
                      placeholder="7321234567"
                    />
                  </div>
                  <div>
                    <Label htmlFor="whatsapp">WhatsApp</Label>
                    <Input
                      id="whatsapp"
                      value={signature.whatsapp}
                      onChange={(e) => setSignature({...signature, whatsapp: e.target.value})}
                      placeholder="17321234567"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="sig_email">Email</Label>
                  <Input
                    id="sig_email"
                    type="email"
                    value={signature.email}
                    onChange={(e) => setSignature({...signature, email: e.target.value})}
                    placeholder="contact@example.com"
                  />
                </div>
                
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={signature.website}
                    onChange={(e) => setSignature({...signature, website: e.target.value})}
                    placeholder="https://www.example.com"
                  />
                </div>
                
                <div className="pt-2">
                  <Label className="text-slate-500 text-sm">Social Media Links (optional)</Label>
                  <div className="grid grid-cols-1 gap-3 mt-2">
                    <Input
                      value={signature.facebook}
                      onChange={(e) => setSignature({...signature, facebook: e.target.value})}
                      placeholder="Facebook URL"
                    />
                    <Input
                      value={signature.instagram}
                      onChange={(e) => setSignature({...signature, instagram: e.target.value})}
                      placeholder="Instagram URL"
                    />
                    <Input
                      value={signature.twitter}
                      onChange={(e) => setSignature({...signature, twitter: e.target.value})}
                      placeholder="Twitter URL"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button onClick={saveSignature} disabled={savingSignature}>
                    {savingSignature ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Signature
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {showPreview ? 'Hide' : 'Show'} Preview
                  </Button>
                </div>
              </div>
              
              {/* Right Column - Preview */}
              <div>
                {showPreview && (
                  <div className="bg-white border rounded-lg p-6">
                    <p className="text-sm text-slate-500 mb-4 font-medium">Signature Preview:</p>
                    <div className="border-t pt-4">
                      {generateSignaturePreview()}
                    </div>
                  </div>
                )}
                
                {!showPreview && (
                  <div className="bg-slate-50 rounded-lg p-6 text-center text-slate-500">
                    <PenLine className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Click "Show Preview" to see how your signature will appear in emails</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Troubleshooting Guide */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Troubleshooting Guide
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-medium">If RESEND_API_KEY is not configured:</p>
                <ol className="text-sm text-slate-600 mt-2 list-decimal list-inside space-y-1">
                  <li>Go to <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">resend.com</a> and create an account</li>
                  <li>Generate an API key from the dashboard</li>
                  <li>Add RESEND_API_KEY to your deployment environment variables</li>
                  <li>Redeploy your application</li>
                </ol>
              </div>

              <div className="border-l-4 border-amber-500 pl-4">
                <p className="font-medium">If emails go to spam:</p>
                <ol className="text-sm text-slate-600 mt-2 list-decimal list-inside space-y-1">
                  <li>Verify your custom domain in Resend dashboard</li>
                  <li>Add the required DNS records (SPF, DKIM, DMARC)</li>
                  <li>Use a professional "from" address (e.g., no-reply@yourdomain.com)</li>
                </ol>
              </div>

              <div className="border-l-4 border-green-500 pl-4">
                <p className="font-medium">Domain verification status:</p>
                <p className="text-sm text-slate-600 mt-2">
                  {isUsingCustomDomain ? (
                    <>
                      <span className="text-green-600 font-medium">✓ Custom domain configured.</span> 
                      {' '}Make sure it's verified in your Resend dashboard.
                    </>
                  ) : (
                    <>
                      <span className="text-amber-600 font-medium">⚠ Using default Resend domain.</span>
                      {' '}Emails may go to spam. Configure a custom domain for better deliverability.
                    </>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Template Placeholders Reference */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PenLine className="h-5 w-5 text-purple-600" />
              Email Template Placeholders
            </CardTitle>
            <CardDescription>
              Use these placeholders in your email templates and notifications. They will be automatically replaced with actual values when emails are sent.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Recipient Information */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  Recipient Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <PlaceholderItem code="{{name}}" description="Recipient's full name" />
                  <PlaceholderItem code="{{parent_name}}" description="Parent's name (same as {{name}} for parents)" />
                  <PlaceholderItem code="{{email}}" description="Recipient's email address" />
                  <PlaceholderItem code="{{role}}" description="User's role (parent, coach, staff, admin)" />
                </div>
              </div>

              {/* Player Information */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Player Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <PlaceholderItem code="{{player_name}}" description="Player's full name" />
                  <PlaceholderItem code="{{player_names}}" description="Comma-separated list of all player names" />
                  <PlaceholderItem code="{{age}}" description="Player's age (for birthday emails)" />
                </div>
              </div>

              {/* Academy/Business Information */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                  Academy Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <PlaceholderItem code="{{academy_name}}" description="Your academy/business name" />
                  <PlaceholderItem code="{{login_url}}" description="Website/login URL" />
                </div>
              </div>

              {/* Program & Class Information */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Program & Class Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <PlaceholderItem code="{{program_name}}" description="Name of the program" />
                  <PlaceholderItem code="{{class_name}}" description="Name of the class" />
                  <PlaceholderItem code="{{schedule}}" description="Class schedule" />
                  <PlaceholderItem code="{{location}}" description="Class/session location" />
                  <PlaceholderItem code="{{start_date}}" description="Enrollment or event start date" />
                  <PlaceholderItem code="{{end_date}}" description="Enrollment or event end date" />
                </div>
              </div>

              {/* Billing & Payment */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                  Billing & Payment
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <PlaceholderItem code="{{amount}}" description="Payment or invoice amount" />
                  <PlaceholderItem code="{{monthly_fee}}" description="Monthly subscription fee" />
                  <PlaceholderItem code="{{due_date}}" description="Payment due date" />
                  <PlaceholderItem code="{{invoice_number}}" description="Invoice number/ID" />
                  <PlaceholderItem code="{{payment_method}}" description="Payment method used" />
                  <PlaceholderItem code="{{balance}}" description="Outstanding balance" />
                  {billingPlaceholders.filter((p) => !LISTED_ELSEWHERE.has(p.key)).map((p) => (
                    <PlaceholderItem key={p.key} code={p.key} description={p.description} />
                  ))}
                </div>
              </div>

              {/* Tournament Information */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Tournament Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <PlaceholderItem code="{{tournament_name}}" description="Name of the tournament" />
                  <PlaceholderItem code="{{tournament_date}}" description="Tournament date(s)" />
                  <PlaceholderItem code="{{registration_deadline}}" description="Registration deadline" />
                  <PlaceholderItem code="{{team_name}}" description="Team name" />
                  <PlaceholderItem code="{{age_category}}" description="Age category (U11, U13, etc.)" />
                </div>
              </div>

              {/* Promotional */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-pink-500 rounded-full"></span>
                  Promotional
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <PlaceholderItem code="{{discount_code}}" description="Promotional discount code" />
                  <PlaceholderItem code="{{discount_percentage}}" description="Discount percentage (e.g., 20)" />
                  <PlaceholderItem code="{{offer_expiry}}" description="Offer expiration date" />
                </div>
              </div>

              {/* Account & Authentication */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-500 rounded-full"></span>
                  Account & Authentication
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <PlaceholderItem code="{{password}}" description="Temporary password (welcome emails)" />
                  <PlaceholderItem code="{{reset_link}}" description="Password reset link" />
                  <PlaceholderItem code="{{verification_code}}" description="Email verification code" />
                </div>
              </div>

              {/* Usage Tips */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <h4 className="font-semibold text-blue-900 mb-2">Usage Tips</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Placeholders are case-sensitive - use exactly as shown above</li>
                  <li>• Placeholders must include the double curly braces: {`{{placeholder}}`}</li>
                  <li>• Not all placeholders are available for every email type</li>
                  <li>• If a placeholder value is not available, it may show as empty or default text</li>
                  <li>• Test your templates before sending to ensure placeholders render correctly</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

// Helper component for displaying placeholder items
const PlaceholderItem = ({ code, description }) => (
  <div className="flex items-start gap-2 p-2 bg-slate-50 rounded-lg">
    <code className="bg-slate-200 text-slate-800 px-2 py-0.5 rounded text-sm font-mono whitespace-nowrap">
      {code}
    </code>
    <span className="text-sm text-slate-600">{description}</span>
  </div>
);

export default EmailSettings;

// iter340 — Branded sending identity: per-tenant verified Resend domain
// + From/Reply-To. Falls back to the platform default until verified.
function EmailIdentityCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ domain: '', from_name: '', from_address: '', reply_to: '' });

  const load = async () => {
    try {
      const res = await api.get('/admin/email-identity');
      setData(res.data);
    } catch { /* keep null */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post('/admin/email-identity', form);
      toast.success('Domain added. Publish the DNS records below, then click Verify.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add domain'); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const res = await api.post('/admin/email-identity/verify');
      const st = res.data.domain_status;
      if (st === 'verified') toast.success('Domain verified! Your emails now send from your domain.');
      else toast.info(`Status: ${st}. DNS can take up to 24–48h to propagate. Try again shortly.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Verification failed'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Remove your sending domain and revert to the platform default From/Reply-To?')) return;
    setBusy(true);
    try { await api.delete('/admin/email-identity'); toast.success('Reverted to platform default'); setData(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
    finally { setBusy(false); }
  };

  if (loading) return null;
  const configured = data?.configured;
  const status = data?.domain_status;
  const records = data?.records || [];
  const statusColor = status === 'verified' ? 'bg-green-100 text-green-700'
    : status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800';

  return (
    <Card data-testid="email-identity-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" /> Branded Sending Domain
        </CardTitle>
        <CardDescription>
          Send customer emails from your own verified domain with your own Reply-To. Until a domain is
          verified, emails keep sending from your current address and replies default to {data?.platform_default_from ? 'the platform Reply-To' : 'support@vyomos.io'}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!configured && (
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Sending domain (subdomain recommended)</Label>
              <Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="mail.youracademy.com" data-testid="identity-domain-input" />
            </div>
            <div>
              <Label className="text-xs">From name</Label>
              <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} placeholder="Your Academy" data-testid="identity-fromname-input" />
            </div>
            <div>
              <Label className="text-xs">From address (must use the domain)</Label>
              <Input value={form.from_address} onChange={(e) => setForm({ ...form, from_address: e.target.value })} placeholder="noreply@mail.youracademy.com" data-testid="identity-fromaddr-input" />
            </div>
            <div>
              <Label className="text-xs">Reply-To address</Label>
              <Input value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} placeholder="info@youracademy.com" data-testid="identity-replyto-input" />
            </div>
            <div className="md:col-span-2">
              <Button onClick={create} disabled={busy} data-testid="identity-create-btn">
                <Globe className="h-4 w-4 mr-2" /> {busy ? 'Adding…' : 'Add sending domain'}
              </Button>
            </div>
          </div>
        )}

        {configured && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-medium" data-testid="identity-domain-name">{data.domain_name}</span>
              <Badge className={statusColor} data-testid="identity-status">{status}</Badge>
              <span className="text-sm text-slate-500">From: {data.from_name ? `${data.from_name} <${data.from_address}>` : data.from_address}</span>
              <span className="text-sm text-slate-500">· Reply-To: {data.reply_to}</span>
            </div>
            {status !== 'verified' && (
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
                  Add these DNS records at your domain registrar, then click Verify:
                </div>
                <div className="divide-y">
                  {records.map((r, i) => (
                    <div key={i} className="px-3 py-2 text-xs font-mono grid grid-cols-1 md:grid-cols-[70px_1fr_2fr] gap-2 break-all" data-testid={`identity-dns-${i}`}>
                      <span className="uppercase text-slate-500">{r.type || r.record}</span>
                      <span className="text-slate-700">{r.name}</span>
                      <span className="text-slate-500">{r.value}</span>
                    </div>
                  ))}
                  {records.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Refresh to load DNS records.</div>}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              {status !== 'verified' && (
                <Button onClick={verify} disabled={busy} data-testid="identity-verify-btn">
                  <CheckCircle className="h-4 w-4 mr-2" /> {busy ? 'Checking…' : 'Verify domain'}
                </Button>
              )}
              <Button variant="outline" onClick={load} data-testid="identity-refresh-btn"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
              <Button variant="ghost" className="text-red-600" onClick={remove} disabled={busy} data-testid="identity-remove-btn">Remove</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
